package dev.jfronny.zerointerest.service

import androidx.datastore.core.DataStore
import androidx.datastore.preferences.core.Preferences
import androidx.datastore.preferences.core.booleanPreferencesKey
import androidx.datastore.preferences.core.stringPreferencesKey
import de.connect2x.trixnity.core.model.RoomId
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.Json

class Settings(private val store: DataStore<Preferences>) {
    private val rememberedRoom = stringPreferencesKey("rememberedRoom")
    private val defaultHomeserver = stringPreferencesKey("defaultHomeserver")
    private val flipBalancesKey = booleanPreferencesKey("flipBalances")
    private val debugHintsKey = booleanPreferencesKey("debugHints")
    private val requestFullKeyboardKey = booleanPreferencesKey("requestFullKeyboard")
    private val favoriteRoomsKey = stringPreferencesKey("favoriteRooms")

    private val json = Json

    val flipBalances = store.data.map { it[flipBalancesKey] ?: true }
    val debugHints = store.data.map { it[debugHintsKey] ?: false }
    val requestFullKeyboard = store.data.map { it[requestFullKeyboardKey] ?: false }
    val favoriteRooms = store.data.map { it.favoriteRoomIds() }

    suspend fun setFlipBalances(flip: Boolean) {
        store.updateData {
            it.toMutablePreferences().apply {
                set(flipBalancesKey, flip)
            }
        }
    }

    suspend fun setDebugHints(enabled: Boolean) {
        store.updateData {
            it.toMutablePreferences().apply {
                set(debugHintsKey, enabled)
            }
        }
    }

    suspend fun setRequestFullKeyboard(enabled: Boolean) {
        store.updateData {
            it.toMutablePreferences().apply {
                set(requestFullKeyboardKey, enabled)
            }
        }
    }

    suspend fun rememberRoom(roomId: RoomId) {
        store.updateData {
            it.toMutablePreferences().apply {
                set(rememberedRoom, roomId.full)
            }
        }
    }

    suspend fun rememberedRoom(): RoomId? {
        return store.data.map { it[rememberedRoom]?.let { RoomId(it) } }.first()
    }

    suspend fun clearRememberedRoom() {
        store.updateData {
            it.toMutablePreferences().apply {
                remove(rememberedRoom)
            }
        }
    }

    suspend fun setFavoriteRoom(roomId: RoomId, favorite: Boolean) {
        store.updateData { prefs ->
            prefs.toMutablePreferences().apply {
                val current = prefs.favoriteRoomIds()
                val updated = if (favorite) {
                    if (current.any { it == roomId }) current else current + roomId
                } else {
                    current.filterNot { it == roomId }
                }
                set(favoriteRoomsKey, json.encodeToString(updated.map { it.full }))
            }
        }
    }

    suspend fun clearFavoriteRooms() {
        store.updateData {
            it.toMutablePreferences().apply {
                remove(favoriteRoomsKey)
            }
        }
    }

    private fun Preferences.favoriteRoomIds(): List<RoomId> = this[favoriteRoomsKey]
        ?.let { raw ->
            runCatching { json.decodeFromString<List<String>>(raw) }.getOrDefault(emptyList())
        }
        .orEmpty()
        .map { RoomId(it) }

    suspend fun setDefaultHomeserver(homeserver: String) {
        store.updateData {
            it.toMutablePreferences().apply {
                set(defaultHomeserver, homeserver)
            }
        }
    }

    suspend fun defaultHomeserver(): String {
        return store.data.map { it[defaultHomeserver] ?: FALLBACK_HOMESERVER }.first()
    }

    companion object {
        const val FALLBACK_HOMESERVER = "https://matrix.org"
    }
}
